import React from 'react';
import { Box, Card, CardContent, Grid, Skeleton, Tooltip, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import PersonIcon from '@mui/icons-material/Person';
import BalanceIcon from '@mui/icons-material/Balance';
import SpeedIcon from '@mui/icons-material/Speed';
import { AiUsageByDayDto, AiUsageByProviderDto, AiUsageSummaryDto } from './AiManagementPage';

interface AiAnalyticsInsightCardsProps {
    summary: AiUsageSummaryDto | null;
    loading: boolean;
}

interface InsightCard {
    title: string;
    value: string;
    subtitle: string;
    detail?: string;
    icon: React.ReactNode;
    gradient: string;
    shadow: string;
    badge?: { label: string; color: string };
}

const computeInsights = (summary: AiUsageSummaryDto): InsightCard[] => {
    const daily = summary.DailyBreakdown ?? [];
    const providers = summary.TopProviders ?? [];

    // 1. Peak Day
    const peakDay = daily.reduce<AiUsageByDayDto | null>(
        (max, d) => (!max || d.Requests > max.Requests ? d : max),
        null
    );
    const peakDayLabel = peakDay
        ? new Date(peakDay.Date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
          })
        : '—';
    const peakDayValue = peakDay ? peakDay.Requests.toLocaleString() : '0';

    // 2. Busiest Provider
    const busiest: AiUsageByProviderDto | null = providers[0] ?? null;
    const busiestValue = busiest
        ? `${(busiest.TotalTokens / 1000).toFixed(1)}K`
        : '—';
    const busiestName = busiest ? busiest.ProviderName ?? 'Unknown' : '—';
    const busiestRole = busiest?.RoleName ?? '';

    // 3. Prompt-to-Completion Ratio
    const prompt = summary.TotalPromptTokens ?? 0;
    const completion = summary.TotalCompletionTokens ?? 0;
    const totalForRatio = prompt + completion;
    const promptPct = totalForRatio > 0 ? Math.round((prompt / totalForRatio) * 100) : 0;
    const completionPct = 100 - promptPct;
    const ratioLabel = totalForRatio > 0 ? `${promptPct}% / ${completionPct}%` : '—';
    const ratioSubtitle = 'Prompt tokens vs. completion tokens';

    // 4. Avg Tokens per Request
    const totalRequests = summary.TotalRequests ?? 0;
    const totalTokens = summary.TotalTokens ?? 0;
    const avgTokens = totalRequests > 0 ? Math.round(totalTokens / totalRequests) : 0;

    // 5. Volume Trend (first-half vs second-half of period)
    let trendValue = '—';
    let trendDirection: 'up' | 'down' | 'flat' = 'flat';
    let trendSubtitle = 'Trend over the selected period';
    if (daily.length >= 4) {
        const sorted = [...daily].sort((a, b) => a.Date.localeCompare(b.Date));
        const mid = Math.floor(sorted.length / 2);
        const firstHalf = sorted.slice(0, mid);
        const secondHalf = sorted.slice(mid);
        const firstAvg = firstHalf.reduce((s, d) => s + d.Requests, 0) / firstHalf.length;
        const secondAvg = secondHalf.reduce((s, d) => s + d.Requests, 0) / secondHalf.length;
        if (firstAvg > 0) {
            const changePct = ((secondAvg - firstAvg) / firstAvg) * 100;
            trendValue = `${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}%`;
            trendDirection = changePct > 2 ? 'up' : changePct < -2 ? 'down' : 'flat';
            trendSubtitle =
                trendDirection === 'up'
                    ? 'Volume growing in the selected range'
                    : trendDirection === 'down'
                    ? 'Volume declining in the selected range'
                    : 'Volume holding steady in the selected range';
        }
    }

    const TrendIcon =
        trendDirection === 'up'
            ? TrendingUpIcon
            : trendDirection === 'down'
            ? TrendingDownIcon
            : TrendingFlatIcon;
    const trendGradient =
        trendDirection === 'up'
            ? 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)'
            : trendDirection === 'down'
            ? 'linear-gradient(135deg, #e53935 0%, #ef9a9a 100%)'
            : 'linear-gradient(135deg, #546e7a 0%, #90a4ae 100%)';
    const trendShadow =
        trendDirection === 'up'
            ? '0 8px 24px rgba(67, 160, 71, 0.28)'
            : trendDirection === 'down'
            ? '0 8px 24px rgba(229, 57, 53, 0.28)'
            : '0 8px 24px rgba(84, 110, 122, 0.25)';

    return [
        {
            title: 'Peak Day',
            value: peakDayValue,
            subtitle: `requests on ${peakDayLabel}`,
            detail: peakDay ? `${peakDay.TotalTokens.toLocaleString()} total tokens` : undefined,
            icon: <WhatshotIcon sx={{ fontSize: 32, opacity: 0.85 }} />,
            gradient: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
            shadow: '0 8px 24px rgba(245, 124, 0, 0.28)',
        },
        {
            title: 'Busiest Provider',
            value: busiestValue,
            subtitle: `tokens — ${busiestName}`,
            detail: busiestRole ? `Role: ${busiestRole}` : undefined,
            icon: <PersonIcon sx={{ fontSize: 32, opacity: 0.85 }} />,
            gradient: 'linear-gradient(135deg, #5c6bc0 0%, #9575cd 100%)',
            shadow: '0 8px 24px rgba(92, 107, 192, 0.28)',
        },
        {
            title: 'Prompt / Completion Split',
            value: ratioLabel,
            subtitle: ratioSubtitle,
            detail:
                totalForRatio > 0
                    ? `${prompt.toLocaleString()} prompt · ${completion.toLocaleString()} completion`
                    : undefined,
            icon: <BalanceIcon sx={{ fontSize: 32, opacity: 0.85 }} />,
            gradient: 'linear-gradient(135deg, #00838f 0%, #4dd0e1 100%)',
            shadow: '0 8px 24px rgba(0, 131, 143, 0.28)',
        },
        {
            title: 'Avg Tokens / Request',
            value: avgTokens > 0 ? avgTokens.toLocaleString() : '—',
            subtitle: 'Efficiency metric for the period',
            detail:
                totalRequests > 0
                    ? `${totalRequests.toLocaleString()} requests · ${totalTokens.toLocaleString()} total tokens`
                    : undefined,
            icon: <SpeedIcon sx={{ fontSize: 32, opacity: 0.85 }} />,
            gradient: 'linear-gradient(135deg, #ad1457 0%, #f06292 100%)',
            shadow: '0 8px 24px rgba(173, 20, 87, 0.28)',
        },
        {
            title: 'Projected Cost (EOM)',
            value: `$${(summary.ProjectedMonthlyCostUsd ?? 0).toFixed(4)}`,
            subtitle: 'Based on range run rate',
            detail: 'Average daily cost extrapolated to a full calendar month',
            icon: <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.85 }} />,
            gradient: 'linear-gradient(135deg, #7b1fa2 0%, #e040fb 100%)',
            shadow: '0 8px 24px rgba(123, 31, 162, 0.28)',
        },
        {
            title: 'Volume Trend',
            value: trendValue,
            subtitle: trendSubtitle,
            detail:
                daily.length >= 4
                    ? `Based on first vs. second half of the period`
                    : 'Not enough data for trend analysis',
            icon: <TrendIcon sx={{ fontSize: 32, opacity: 0.85 }} />,
            gradient: trendGradient,
            shadow: trendShadow,
        },
    ];
};

const AiAnalyticsInsightCards: React.FC<AiAnalyticsInsightCardsProps> = ({ summary, loading }) => {
    if (loading) {
        return (
            <Grid container spacing={3}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 'auto' }} key={i} sx={{ flex: '1 1 180px' }}>
                        <Card
                            elevation={3}
                            sx={{
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                                height: 148,
                            }}
                        >
                            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                <Skeleton variant="text" width="55%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.4)', mb: 1 }} />
                                <Skeleton variant="text" width="70%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                                <Skeleton variant="text" width="85%" sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (!summary) {
        return null;
    }

    const cards = computeInsights(summary);

    return (
        <Grid container spacing={3}>
            {cards.map((card, index) => (
                <Grid size={{ xs: 12, sm: 6, lg: 'auto' }} key={index} sx={{ flex: '1 1 180px' }}>
                    <Tooltip title={card.detail ?? ''} placement="top" arrow disableHoverListener={!card.detail}>
                        <Card
                            elevation={3}
                            sx={{
                                background: card.gradient,
                                color: '#ffffff',
                                borderRadius: 3,
                                boxShadow: card.shadow,
                                position: 'relative',
                                overflow: 'hidden',
                                height: '100%',
                                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                cursor: card.detail ? 'help' : 'default',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: card.shadow.replace('0.28)', '0.42)').replace('0.25)', '0.38)'),
                                },
                            }}
                        >
                            {/* Decorative circle */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: -24,
                                    top: -24,
                                    width: 110,
                                    height: 110,
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    pointerEvents: 'none',
                                }}
                            />
                            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            opacity: 0.9,
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            fontSize: '0.72rem',
                                            lineHeight: 1.3,
                                            maxWidth: '70%',
                                        }}
                                    >
                                        {card.title}
                                    </Typography>
                                    {card.icon}
                                </Box>
                                <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, lineHeight: 1.1 }}>
                                    {card.value}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.82, fontSize: '0.8rem', lineHeight: 1.35 }}>
                                    {card.subtitle}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Tooltip>
                </Grid>
            ))}
        </Grid>
    );
};

export default AiAnalyticsInsightCards;
